/**
 * AppShell.tsx — MER Algo Footprints v9.2.0
 *
 * Authenticated app shell with left navigation rail and top context bar.
 *
 * ── Layout ────────────────────────────────────────────────────────────────────
 *
 *  ┌─────────────────────────────────────────┐
 *  │ TopContextBar (fixed, h=36)              │
 *  ├──────┬──────────────────────────────────┤
 *  │ Rail │  Workspace content (scrollable)  │
 *  │ (52) │                                  │
 *  └──────┴──────────────────────────────────┘
 *
 * The left rail is 52px wide — icon + abbreviated label.
 * The top bar carries symbol, price, regime, bias, confidence.
 * The content area is the single primary scroll container per workspace.
 *
 * ── Extension points ─────────────────────────────────────────────────────────
 * - Rail icons/labels wired to /app/* routes via react-router NavLink
 * - TopContextBar gets data from the shared symbol store (no direct engine coupling)
 * - The outer layout does not know about workspace internals
 */

import React, { useCallback, useMemo } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useStore } from '../../store';
import { useAuth } from '../../auth/AuthContext';
import { getSymbolContext } from '../../selectors/symbolContext';
import { getThesis } from '../../ai/thesisEngine';
import { GlobalTradingHeader } from '../../components/trading/GlobalTradingHeader';
import { ComplianceFooter } from '../../components/trading/ComplianceFooter';
import { FlowDebugPanel } from '../../features/flow/FlowDebugPanel';
import { InstitutionalFlowRuntime } from '../runtime/InstitutionalFlowRuntime';
import { useUserPrefsHydration } from '../../hooks/useUserPrefsHydration';
import { useCapabilityHydration } from '../../hooks/useCapabilityHydration';
import { useShadowObservation } from '../../hooks/useShadowObservation';

// ── Feature flag ──────────────────────────────────────────────────────────────
//
// Phase 2 (v9.2.0): persistent Global Trading Header (HUD).
//
// REACT_APP_NEW_HEADER controls which top bar mounts:
//   'true'  (default) — new 188px GlobalTradingHeader
//   'false'           — legacy 36px TopContextBar (kept as fallback)
//
// CRA exposes process.env vars prefixed with REACT_APP_ to the browser bundle.
// Set REACT_APP_NEW_HEADER=false in Render env vars to revert without a code
// change. Production never sees this branch, so the flag is dev-only.

const NEW_HEADER_ENABLED =
  (process.env.REACT_APP_NEW_HEADER ?? 'true').toLowerCase() === 'true';

const TOP_BAR_HEIGHT = NEW_HEADER_ENABLED ? 188 : 36;

// Compliance footer (~24px tall: 7px padding × 2 + 10px line + a hair).
// Restored from v8.1.1 (was lost when v9.1.1 react-router shell took over
// from the legacy state-based App.tsx).
const FOOTER_HEIGHT = 28;

// ── Left rail items ───────────────────────────────────────────────────────────

interface RailItem {
  path:  string;
  label: string;
  icon:  string;
  title: string;
}

const RAIL_ITEMS: RailItem[] = [
  { path: '/app',          label: 'CC',      icon: '⬡', title: 'Command Center' },
  { path: '/app/structure',label: 'STR',     icon: '▣', title: 'Structure'      },
  { path: '/app/signals',  label: 'SIG',     icon: '◈', title: 'Signals'        },
  { path: '/app/replay',   label: 'REPLAY',  icon: '↺', title: 'Replay'         },
  { path: '/app/journal',  label: 'LOG',     icon: '📋', title: 'Journal'        },
  { path: '/app/execution',label: 'EXEC',    icon: '⚡', title: 'Execution'      },
  { path: '/app/portfolio',label: 'PORT',    icon: '◉', title: 'Portfolio'      },
  // Phase-2 P2-D4. The password-change surface needs a way in, or the
  // capability exists and no user can reach it. Password change only — the
  // broader account-settings surface stays P2-D14 work (D-P2AA-5).
  { path: '/app/security', label: 'SEC',     icon: '🔑', title: 'Security'       },
];

// ── Top context bar view-model ───────────────────────────────────────────────
//
// Centralises all data access for the top bar.
// The TopContextBar component only receives this flat VM — no direct engine calls.
//
// Replay-safe: deterministic from the cached SymbolContext + SymbolThesis snapshots.

interface TopContextBarVM {
  symbol:    string;
  price:     string;
  regime:    string;
  bias:      string;
  biasColor: string;
  confidence: string;
}

function buildTopContextBarVM(
  sym:    string,
  ctx:    ReturnType<typeof getSymbolContext>,
  thesis: ReturnType<typeof getThesis> | null,
): TopContextBarVM {
  const price  = ctx?.curPrice != null ? ctx.curPrice.toFixed(2) : '—';
  const regime = ctx?.regime?.label ?? '—';
  const bias   = thesis?.current?.overallBias ?? ctx?.regime?.posture ?? '—';
  const conf   = ctx?.regime?.confidence != null ? `${ctx.regime.confidence}%` : '—';
  const biasColor = bias === 'bullish' ? '#4ADE80'
    : bias === 'bearish' ? '#F87171'
    : '#9BAFC8';
  return { symbol: sym || '', price, regime, bias, biasColor, confidence: conf };
}

// ── Top context bar ───────────────────────────────────────────────────────────

function TopContextBar() {
  const { activeSymbol, symbolData, settings, weights } = useStore();
  const sym = activeSymbol ?? '';
  const ctx    = sym ? getSymbolContext(sym, symbolData[sym], settings, weights) : null;
  const thesis = sym ? getThesis(sym) : null;
  // All display derivation is isolated in the VM builder — component is purely presentational
  const vm = useMemo(
    () => buildTopContextBarVM(sym, ctx, thesis),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sym, ctx?.curPrice, ctx?.regime?.label, ctx?.regime?.confidence, thesis?.current?.overallBias],
  );

  return (
    <div style={{
      position: 'fixed', top: 0, left: 52, right: 0, height: 36, zIndex: 900,
      display: 'flex', alignItems: 'center', gap: 0,
      background: 'rgba(8,15,28,0.97)',
      borderBottom: '1px solid #1A3050',
      fontFamily: "'Courier New', monospace",
      fontSize: 11,
    }}>
      {/* Symbol + price */}
      <div style={{ padding: '0 16px', borderRight: '1px solid #1A3050', height: '100%', display: 'flex', alignItems: 'center', minWidth: 90 }}>
        <span style={{ color: '#C9A84C', fontWeight: 700, letterSpacing: 1 }}>
          {vm.symbol || 'NO SYMBOL'}
        </span>
        <span style={{ color: '#9BAFC8', marginLeft: 8 }}>{vm.price}</span>
      </div>

      {/* Regime */}
      <div style={{ padding: '0 14px', borderRight: '1px solid #1A3050', height: '100%', display: 'flex', alignItems: 'center', gap: 6, minWidth: 160 }}>
        <span style={{ color: '#4A6FA5', fontSize: 9, letterSpacing: 1 }}>REGIME</span>
        <span style={{ color: '#E8EDF5' }}>{vm.regime}</span>
      </div>

      {/* Bias */}
      <div style={{ padding: '0 14px', borderRight: '1px solid #1A3050', height: '100%', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: '#4A6FA5', fontSize: 9, letterSpacing: 1 }}>BIAS</span>
        <span style={{ color: vm.biasColor, textTransform: 'uppercase', fontWeight: 700, fontSize: 10 }}>
          {vm.bias}
        </span>
      </div>

      {/* Confidence */}
      <div style={{ padding: '0 14px', borderRight: '1px solid #1A3050', height: '100%', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: '#4A6FA5', fontSize: 9, letterSpacing: 1 }}>CONF</span>
        <span style={{ color: '#9BAFC8' }}>{vm.confidence}</span>
      </div>

      <div style={{ flex: 1 }} />

      <div style={{ padding: '0 14px', color: '#2A4060', fontSize: 9, letterSpacing: 1 }}>
        MER ALGO FOOTPRINTS
      </div>
    </div>
  );
}

// ── Left rail ─────────────────────────────────────────────────────────────────

function LeftRail() {
  const { logout, entitlement } = useAuth();
  const navigate   = useNavigate();

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/', { replace: true });
  }, [logout, navigate]);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: 52, bottom: 0, zIndex: 950,
      background: '#080F1C',
      borderRight: '1px solid #1A3050',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      paddingTop: 0,
    }}>
      {/* MER logo mark */}
      <div style={{ width: 52, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #1A3050', flexShrink: 0 }}>
        <svg viewBox="0 0 20 24" width="18" height="22">
          <path d="M1 1 L19 1 L19 15 Q19 22 10 24 Q1 22 1 15 Z" fill="#0D1F35" stroke="#C9A84C" strokeWidth="1"/>
          <polyline points="3,18 7,13 10,9 13,11 17,14" fill="none" stroke="#C9A84C" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {/* Nav items */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8, gap: 2, width: '100%' }}>
        {RAIL_ITEMS.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/app'}
            title={item.title}
            style={({ isActive }) => ({
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              width: '100%', height: 48, gap: 3,
              textDecoration: 'none',
              background: isActive ? 'rgba(201,168,76,0.08)' : 'transparent',
              borderRight: isActive ? '2px solid #C9A84C' : '2px solid transparent',
              color: isActive ? '#C9A84C' : '#3A5070',
              transition: 'all 0.12s',
            })}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>{item.icon}</span>
            <span style={{ fontSize: 7.5, letterSpacing: 0.5, fontFamily: "'Courier New', monospace", fontWeight: 700 }}>
              {item.label}
            </span>
          </NavLink>
        ))}
        {entitlement.isAdmin && (
          <NavLink
            to="/app/admin/users"
            title="Admin"
            style={({ isActive }) => ({
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              width: '100%', height: 48, gap: 3, marginTop: 8,
              textDecoration: 'none',
              background: isActive ? 'rgba(167,139,250,0.08)' : 'transparent',
              borderRight: isActive ? '2px solid #A78BFA' : '2px solid transparent',
              borderTop: '1px solid #1A3050',
              color: isActive ? '#A78BFA' : '#3A5070',
              transition: 'all 0.12s',
            })}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>*</span>
            <span style={{ fontSize: 7.5, letterSpacing: 0.5, fontFamily: "'Courier New', monospace", fontWeight: 700 }}>
              ADMIN
            </span>
          </NavLink>
        )}
      </div>

      {/* Sign out (bottom) */}
      <button
        onClick={handleLogout}
        title="Sign out"
        style={{
          width: '100%', height: 40, background: 'none', border: 'none',
          borderTop: '1px solid #1A3050', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#2A4060', fontSize: 14,
        }}
      >
        ⏻
      </button>
    </div>
  );
}

// ── AppShell ──────────────────────────────────────────────────────────────────

export function AppShell() {
  // Phase 3: hydrate user prefs (currently just trader horizon) from
  // Postgres on auth transition. Side-effect only; no rendering impact.
  useUserPrefsHydration();

  // P1-Q4 G-CLIENT-1 (position 8): one-shot advisory capability hydration on the
  // same authenticated-shell transition. Side-effect only; no rendering impact,
  // no market-data effect, no shadow observation.
  useCapabilityHydration();

  // P1-Q4 G-CLIENT-4C (position 9): canonical-pair shadow observation dispatch.
  // Independent of the capability hydration above; side-effect only, no rendering
  // impact, no market-data effect, no UI, and no store write.
  useShadowObservation();

  return (
    <div className="mer-app-shell" style={{ display: 'flex', flexDirection: 'column', background: '#080F1C' }}>
      <LeftRail />
      {NEW_HEADER_ENABLED ? <GlobalTradingHeader /> : <TopContextBar />}

      {/* Workspace content area — offset for rail (52px), top bar, and footer */}
      <div style={{ marginLeft: 52, paddingTop: TOP_BAR_HEIGHT, paddingBottom: FOOTER_HEIGHT, flex: '1 1 auto', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Outlet />
      </div>

      {/* Phase 1 hardening: institutional flow runtime drives polling
          independently of any UI surface. Headless; writes to the AI
          thesis sidecar (promptBuilder.setLiveFlowContext) so flow
          context is available regardless of panel state. */}
      <InstitutionalFlowRuntime />

      {/* Phase 1 Session D: institutional flow debug panel.
          Display-only; subscribes to the sidecar. Disabled by default;
          enable via REACT_APP_FLOW_DEBUG_ENABLED=true in dev. */}
      <FlowDebugPanel />

      {/* Compliance footer — restored from v8.1.1 */}
      <ComplianceFooter />
    </div>
  );
}
